import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import moment from 'moment';
import {
  Assignment,
  CreateAssignmentInput,
  BatchUpdateAssignmentInput,
  WeeklyPlanStateData,
  StudentAssignments,
  RootState,
  BatchAddAssignmentInput,
  CurriculumLessonResponse,
  SkillMasteryRecord,
  CRUDAssignmentQueue,
  Story,
  SkillStandardResponse,
  SkillMetadata,
  Skill,
  parseMasteryPacingToClassName,
} from "@/models";
import { ChangeInMastery, PlannerCurriculum, PlannerCurriculumTrack } from "@/features/planner/PlannerTypes";
import {
  AssignmentStatus,
  AssignmentType,
  Entity,
  GradeNumericToString,
  MasteryPacing,
  MasteryPacingColor,
  MasteryStatusPacing,
  ResourceType,
  SkillType
} from "@/enums";
import { AsyncLoadingState } from "@/features/dashboard/enums/common";
import {
  aggregateAssignmentsToLessons,
  aggregateUpdatedLessonByPlanId,
  getDefaultManifest,
  getMostCommonGrade,
  getTimeAssignedByGrade
} from "@/utilities";
import { getMondayFridayDates } from "@/features/dashboard/utils";

// Store
import { createSelector } from '@reduxjs/toolkit';

// - Queries
import { getTeacherAssignments } from "@/services/weeklyPlanner/query/getTeacherAssignments";
import { getSkillsMetadata } from "@/services/weeklyPlanner/query/getSkillsMetadata";
import { getStory } from "@/services/weeklyPlanner/query/getStory";
import { getStudentSkillsMastery } from "@/services/weeklyPlanner/query/getStudentSkillsMastery";
import { getCurriculum } from "@/services/weeklyPlanner/query/getCurriculum";
import { AmiraResources, AmiraResourcesMetadataResponse, AmiraResourcesResponse } from "@/models/AmiraResources.interface";
import { getSkillResources } from "@/services/weeklyPlanner/query/getSkillResources";
import { getStudentSkillStandards } from "@/services/weeklyPlanner/query/getStudentSkillStandards";
import { getStudentAssignments } from "@/services/weeklyPlanner/query/getStudentAssignments";

// - Mutations
import { createAssignmentMutation } from "@/services/weeklyPlanner/mutations/createAssignments/createAssignmentMutation";
import { updateAssignmentMutation } from "@/services/weeklyPlanner/mutations/updateAssignmentStatus/updateAssignmentMutation";
import { createAssignmentsMutation } from "@/services/weeklyPlanner/mutations/createAssignments/createAssignmentsMutation";
import { getResourcesMetadata } from "@/services/weeklyPlanner/query/getResourcesMetadata";
import { transformId } from "@/utilities/transformId";

// #region Models
interface StudentSkillsMasteryResponse {
  studentSkillsMastery: {
    records: {
      pacing: string;
      skillId: string;
    }[];
  }[];
}

interface GetTeacherAssignmentsResponse {
  assignment: Assignment[];
}

interface GetStudentAssignmentsResponse {
  teacherClassInbox: {
    assignments: Assignment[];
    studentAssignments: StudentAssignments[];
  };
}

export interface GetStoryResponse {
  getStoryGuide: Story;
}

export interface WeeklyPlanState {
  loading: AsyncLoadingState;
  data: WeeklyPlanStateData | null;
  error: string | null; // if not empty, then we'll show reload button.
  savingData: AsyncLoadingState;
  skillsMasteryLoaded: boolean;
  skillsMasteryLoading: { [key: string]: boolean };
  fetchTeacherAssignLoading: AsyncLoadingState;
  fetchStudentAssignLoading: AsyncLoadingState;
  fetchCurriculumLoading: AsyncLoadingState;
  fetchMasteryLoading: AsyncLoadingState;
  fetchSearchStoriesLoading: AsyncLoadingState;
  fetchSkillsMetadataLoading: AsyncLoadingState;
  aggregateLessons: AsyncLoadingState;
  updateAssignmentDateFrom: AsyncLoadingState;
  mutateAssignmentsLoading: AsyncLoadingState;
  fetchSkillResourcesLoading: number;
  fetchStudentMasteryLoading: number;
  fetchResourcesMetadataLoading: AsyncLoadingState;
  // Track initial fetch status per classroom to prevent multiple initial fetches
  initialFetchDispatched: { [classroomId: string]: boolean };
}
// #endregion Models

let [dateFrom, dateTo] = getMondayFridayDates();

export const defaultWeeklyPlanState: WeeklyPlanStateData = {
  studentAssignmentInfo: [],
  assignments: [],
  resources: [],
  dateFrom,
  dateTo,
  curriculumInfo: [],
  curriculum: {
    name: "",
    id: "",
    dateFrom,
    dateTo,
    tracks: [],
  },
  curTrack: { label: "", value: "" },
  stories: {},
  mastery: {},
  resourcesFetchQueue: [],
  masteryFetchQueue: [],
  crudAssignmentQueue: [],
  crudResponseByLessonPlanId: [],
  searchedStories: [],
  fetchSkillResourcesLoading: 0,
  mutateAssignmentsLoading: AsyncLoadingState.NONE,
  students: [],
};

export const initialWeeklyPlanState: WeeklyPlanState = {
  data: { ...defaultWeeklyPlanState },
  loading: AsyncLoadingState.NONE,
  error: null,
  savingData: AsyncLoadingState.NONE,
  skillsMasteryLoaded: false,
  skillsMasteryLoading: {},
  fetchTeacherAssignLoading: AsyncLoadingState.NONE,
  fetchStudentAssignLoading: AsyncLoadingState.NONE,
  fetchCurriculumLoading: AsyncLoadingState.NONE,
  fetchMasteryLoading: AsyncLoadingState.NONE,
  fetchSearchStoriesLoading: AsyncLoadingState.NONE,
  aggregateLessons: AsyncLoadingState.NONE,
  updateAssignmentDateFrom: AsyncLoadingState.NONE,
  mutateAssignmentsLoading: AsyncLoadingState.NONE,
  fetchSkillResourcesLoading: 0,
  fetchSkillsMetadataLoading: AsyncLoadingState.NONE,
  fetchStudentMasteryLoading: 0,
  fetchResourcesMetadataLoading: AsyncLoadingState.NONE,
  initialFetchDispatched: {},
};

export const getFetchAssignmentInputs = (storeState: RootState) => {
  const isDate = /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/;
  const { weeklyPlan, reports, userData } = storeState;
  const teacherId = userData?.userId;
  const classroomId = reports?.roster?.ui?.selections?.classroom?.id;
  const { dateFrom, dateTo } = weeklyPlan.data!;

  if (
    !isDate.test(dateFrom) ||
    !isDate.test(dateTo) ||
    !classroomId ||
    !teacherId
  ) {
    console.error(
      `invalid inputs dateTo: ${dateTo}, dateFrom: ${dateFrom}, classroomId: ${classroomId}`
    );
    return null;
  }
  const mostCommonGrade = getMostCommonGrade(reports.roster.data.student);
  return {
    classroomId,
    dateFrom: moment(dateFrom).add(-30, 'days').format('YYYY-MM-DD'), 
    dateTo: moment(dateFrom).add(36, 'days').format('YYYY-MM-DD'), // need to fetch more than 1 week of assignments, because CLASS assignments can have future dates
    grade: mostCommonGrade ? GradeNumericToString[mostCommonGrade.toString()] : undefined
  };
};

export const fetchTeacherAssignments = createAsyncThunk<
  GetTeacherAssignmentsResponse,
  { classroomId: string; dateFrom: string; dateTo: string; grade?: string }
>(
  "weeklyPlan/fetchTeacherAssignments",
  async ({ classroomId, dateFrom, dateTo, grade = undefined }, { rejectWithValue }) => {
    try {
      const response = (await getTeacherAssignments(
        classroomId,
        dateFrom,
        dateTo,
        grade
      )) as GetTeacherAssignmentsResponse;
      return response;
    } catch (error: any) {
      console.error(
        "Error fetching district and class assignments data",
        error
      );
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStudentAssignments = createAsyncThunk<
  GetStudentAssignmentsResponse,
  { classroomId: string; dateFrom: string; dateTo: string }
>(
  "weeklyPlan/fetchStudentAssignments",
  async ({ classroomId, dateFrom, dateTo }, { rejectWithValue }) => {
    try {
      const response = (await getStudentAssignments(
        classroomId,
        dateFrom,
        dateTo
      )) as GetStudentAssignmentsResponse;
      return response;
    } catch (error: any) {
      console.error("Error fetching student assignments data", error);
      return rejectWithValue(error.message);
    }
  }
);

// New thunk to coordinate initial fetch and prevent multiple calls
export const initializeWeeklyPlanData = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  "weeklyPlan/initializeWeeklyPlanData",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const fetchInputs = getFetchAssignmentInputs(state);
      
      if (!fetchInputs) {
        return rejectWithValue("Invalid fetch inputs");
      }

      const { classroomId } = fetchInputs;
      const { weeklyPlan } = state;
      
      // Check if initial fetch already dispatched for this classroom
      if (weeklyPlan.initialFetchDispatched[classroomId]) {
        return; // Early return if already dispatched
      }

      // Check if fetches are already in progress or completed
      if (
        weeklyPlan.fetchTeacherAssignLoading !== AsyncLoadingState.NONE ||
        weeklyPlan.fetchStudentAssignLoading !== AsyncLoadingState.NONE
      ) {
        return; // Early return if already loading
      }

      // Check if we already have assignments for this week
      if (weeklyPlan.data?.assignments?.length) {
        return; // Early return if we already have data
      }

      // Mark as dispatched before starting the fetches
      dispatch(markInitialFetchDispatched({ classroomId }));

      // Dispatch both fetch actions
      dispatch(fetchTeacherAssignments(fetchInputs));
      dispatch(fetchStudentAssignments(fetchInputs));
    } catch (error: any) {
      console.error("Error initializing weekly plan data", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCurriculum = createAsyncThunk<
  { getCurriculumInfoForLessons: CurriculumLessonResponse[] },
  { lessonPlanIds: string[] }
>(
  "weeklyPlan/fetchCurriculum",
  async ({ lessonPlanIds }, { rejectWithValue }) => {
    if (!lessonPlanIds || lessonPlanIds.length === 0) {
      return rejectWithValue("No lesson plan IDs provided");
    }
    try {
      const response = (await getCurriculum(lessonPlanIds)) as {
        getCurriculumInfoForLessons: CurriculumLessonResponse[];
      };
      return response;
    } catch (error: any) {
      console.error("Error fetching curriculum data", error);
      return rejectWithValue(error.message);
    }
  }
);


// TODO this possibly will change to fetch teacher resources and stories all from one service which may be different endpoint/service TBD
export const fetchStoryData = createAsyncThunk<
  GetStoryResponse,
  { skillIds: string[]; activityType?: string }
>(
  "weeklyPlan/fetchStoryData",
  async ({ skillIds, activityType }, { rejectWithValue }) => {
    try {
      const response = (await getStory(
        skillIds,
        activityType
      )) as GetStoryResponse;
      return response;
    } catch (error: any) {
      console.error("Error fetching story data", error);
      return rejectWithValue(error.message);
    }
  }
);

export const createAssignment = createAsyncThunk<
  { addAssignment: Assignment },
  { assignment: CreateAssignmentInput }
>(
  "weeklyPlan/createAssignment",
  async ({ assignment }, { rejectWithValue }) => {
    try {
      const response = await createAssignmentMutation(assignment);
      return response;
    } catch (error: any) {
      console.error("Error creating an assignment", error);
      return rejectWithValue(error.message);
    }
  }
);

export const batchUpdateAssignments = createAsyncThunk<
  { batchUpdateAssignment: Assignment[] },
  {
    assignments: BatchUpdateAssignmentInput[];
  }
>(
  "weeklyPlan/batchUpdateAssignments",
  async ({ assignments }, { rejectWithValue }) => {
    try {
      const response = await updateAssignmentMutation([...assignments]);
      return response;
    } catch (error: any) {
      console.error("Error updating assignments", error);
      return rejectWithValue(error.message);
    }
  }
);

export const batchUnassignAssignments = createAsyncThunk<
  { batchUpdateAssignment: Assignment[] },
  {
    assignments: BatchUpdateAssignmentInput[];
  }
>(
  "weeklyPlan/batchUnassignAssignments",
  async ({ assignments }, { rejectWithValue }) => {
    try {
      const response = await updateAssignmentMutation([...assignments]);
      return response;
    } catch (error: any) {
      console.error("Error unassigning assignments", error);
      return rejectWithValue(error.message);
    }
  }
);

export const batchCreateStudentAssignments = createAsyncThunk<
  { batchAddAssignment: Assignment[] }, // TODO check that this a correct response
  { input: BatchAddAssignmentInput }
>(
  "weeklyPlan/batchCreateStudentAssignments",
  async ({ input }, { rejectWithValue }) => {
    try {
      const response = await createAssignmentsMutation(input);
      return response;
    } catch (error: any) {
      console.error("Error batch creating student assignments", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSkillResources = createAsyncThunk<
  { resources: AmiraResourcesResponse[]; skillId: string },
  {
    input: {
      skillId: string;
      studentId: string;
      filter?: { providers?: string[] };
    };
  }
>("weeklyPlan/fetchSkillResources", async ({ input }, { rejectWithValue }) => {
  try {
    const response = await getSkillResources(input.skillId, input.studentId, 10000, input.filter);
    return {
      resources: response.skillResources.resources,
      skillId: input.skillId,
    };
  } catch (error: any) {
    console.error("Error fetching skill resource", error);
    return rejectWithValue(error.message);
  }
});

export const fetchResourcesMetadata = createAsyncThunk<
  { resourcesMetadata: AmiraResourcesMetadataResponse[];},
  {
    input: {
      resourceUniqueId: string;
      resourceGroupType: string;
    }[];
  }
>("weeklyPlan/fetchResourcesMetadata", async ({ input }, { rejectWithValue }) => {
  try {
    const response = await getResourcesMetadata(input);
    return {
      resourcesMetadata: response.getResourceMetadata,
    };
  } catch (error: any) {
    console.error("Error fetching skill resource", error);
    return rejectWithValue(error.message);
  }
});

// currently for the lesson details page skill subgroup breakdowns
export const fetchStudentMastery = createAsyncThunk<
  { lessonId: string; studentMastery: SkillMasteryRecord[] },
  {
    input: {
      lessonId: string;
      skillId: string[];
      studentId: string[];
      dateTo: string;
      dateFrom: string;
    };
  }
>("weeklyPlan/fetchStudentMastery", async ({ input }, { rejectWithValue }) => {
  try {
    const response = await getStudentSkillsMastery({ ...input });
    return {
      lessonId: input.lessonId,
      studentMastery: response.studentSkillsMastery,
    };
  } catch (error: any) {
    console.error("Error fetching student mastery", error);
    return rejectWithValue(error.message);
  }
});

export const fetchSkillsMetadata = createAsyncThunk<
  SkillMetadata[],
  { skillIds: string[] }
>(
  "weeklyPlan/fetchSkillsMetadata",
  async ({ skillIds }, { rejectWithValue }) => {
    try {
      if (!skillIds || skillIds.length === 0) {
        return rejectWithValue("No skill IDs provided");
      }

      const response = await getSkillsMetadata(skillIds);


      if (!response || !response.skillsMetadata) {
        return rejectWithValue("Invalid response format from skills metadata service");
      }
      return response.skillsMetadata;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch skills metadata";
      return rejectWithValue(errorMessage);
    }
  }
);

// currently for overall lesson mastery graphs on planner page and dashboard widget
export const fetchStudentSkillsMastery = createAsyncThunk<
  {
    lessonStartResponse: StudentSkillsMasteryResponse;
    lessonTodayResponse: StudentSkillsMasteryResponse;
    lessonPlanId: string;
    skillsCount: number;
  },
  {
    studentId: number[];
    skillId: string[];
    lessonStartDate: string;
    lessonPlanId: string;
  }
>(
  "weeklyPlan/fetchStudentSkillsMastery",
  async (
    { studentId, skillId, lessonStartDate, lessonPlanId },
    { rejectWithValue }
  ) => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const [lessonStartResponse, lessonTodayResponse] = await Promise.all([
        getStudentSkillsMastery({
          studentId,
          skillId,
          dateTo: lessonStartDate,
          dateFrom: lessonStartDate,
        }),
        getStudentSkillsMastery({
          studentId,
          skillId,
          dateTo: today,
          dateFrom: today,
        }),
      ]);
      return {
        lessonStartResponse,
        lessonTodayResponse,
        lessonPlanId,
        skillsCount: skillId.length,
      };
    } catch (error: any) {
      console.error("Error fetching student skills mastery data", error);
      return rejectWithValue(error.message);
    }
  }
);

// Add new interface for batch operations
interface BatchSkillStandardsRequest {
  skillId: string;
  studentId: string;
  lessonPlanId: string;
}

interface BatchSkillStandardsResponse {
  requests: BatchSkillStandardsRequest[];
  responses: { 
    request: BatchSkillStandardsRequest;
    data: SkillStandardResponse[];
  }[];
}

// Helper function to check if skill standards already exist in store
const hasSkillStandardsInStore = (
  curriculum: PlannerCurriculum,
  skillId: string,
  studentId: string,
  lessonPlanId: string
): boolean => {
  for (const track of curriculum.tracks) {
    for (const unit of track.units) {
      for (const lesson of unit.lessons) {
        if (lesson.lessonPlanId === lessonPlanId) {
          return lesson.skillStandards?.some(ss => 
            ss.skill?.skillId === skillId && 
            ss.standards?.length > 0 // Check if we have standards for this skill
          ) ?? false;
        }
      }
    }
  }
  return false;
};

/**
 * Batched version of fetchStudentSkillStandards that optimizes multiple skill standards requests.
 * 
 * Benefits:
 * - Prevents duplicate API calls by checking if data already exists in store
 * - Deduplicates requests by skillId + studentId combination
 * - Batches all API calls using Promise.all
 * - Updates store only once, reducing unnecessary renders
 * - Automatically filters out requests that already have data cached
 * 
 * Usage:
 * ```typescript
 * const requests = [
 *   { skillId: 'skill1', studentId: 'student1', lessonPlanId: 'lesson1' },
 *   { skillId: 'skill2', studentId: 'student1', lessonPlanId: 'lesson1' },
 * ];
 * dispatch(fetchBatchStudentSkillStandards(requests));
 * ```
 * 
 * @param requests Array of skill standards requests
 * @returns Promise that resolves with batched responses
 */
export const fetchBatchStudentSkillStandards = createAsyncThunk<
  BatchSkillStandardsResponse,
  BatchSkillStandardsRequest[],
  { state: RootState }
>(
  "weeklyPlan/fetchBatchStudentSkillStandards",
  async (requests, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const curriculum = state.weeklyPlan.data?.curriculum;
      
      if (!curriculum) {
        return rejectWithValue("No curriculum data available");
      }

      // Filter out requests that already have data in store
      const filteredRequests = requests.filter(request => 
        !hasSkillStandardsInStore(curriculum, request.skillId, request.studentId, request.lessonPlanId)
      );

      if (filteredRequests.length === 0) {
        // All data already exists, return empty response
        return { requests: [], responses: [] };
      }

      // Deduplicate requests by skillId + studentId combination
      const uniqueRequests = new Map<string, BatchSkillStandardsRequest>();
      filteredRequests.forEach(request => {
        const key = `${request.skillId}-${request.studentId}-${request.lessonPlanId}`;
        if (!uniqueRequests.has(key)) {
          uniqueRequests.set(key, request);
        }
      });

      // Batch all API calls
      const apiPromises = Array.from(uniqueRequests.values()).map(async (request) => {
        try {
          const response = await getStudentSkillStandards({
            skillId: request.skillId,
            studentId: request.studentId,
          });
          return {
            request,
            data: response.getStudentSkillStandards,
          };
        } catch (error) {
          console.error(`Error fetching standards for skill ${request.skillId}, student ${request.studentId}:`, error);
          return {
            request,
            data: [],
          };
        }
      });

      const responses = await Promise.all(apiPromises);

      return {
        requests: Array.from(uniqueRequests.values()),
        responses,
      };
    } catch (error: any) {
      console.error("Error in batch fetch student skill standards", error);
      return rejectWithValue(error.message);
    }
  }
);

const calculatePacing = (
  data: { records: { pacing: string }[] }[],
  count: number
): {
  label: string;
  color: string;
  value: number;
}[] => {
  // undeveloped, developing, mastered, no_data - values from en-us.js and es-mx.js
  const result = {
    [MasteryPacing.AHEAD_OF_GRADE_LEVEL]: {
      label: MasteryStatusPacing.LIKELY_MASTERED,
      color: MasteryPacingColor[MasteryPacing.AHEAD_OF_GRADE_LEVEL],
      value: 0,
    },
    [MasteryPacing.AT_GRADE_LEVEL]: {
      label: MasteryStatusPacing.DEVELOPING,
      color: MasteryPacingColor[MasteryPacing.AT_GRADE_LEVEL],
      value: 0,
    },
    [MasteryPacing.BELOW_GRADE_LEVEL]: {
      label: MasteryStatusPacing.NOT_DEVELOPED,
      color: MasteryPacingColor[MasteryPacing.BELOW_GRADE_LEVEL],
      value: 0,
    },
    [MasteryPacing.NO_DATA]: {
      label: MasteryStatusPacing.NO_DATA,
      color: MasteryPacingColor[MasteryPacing.NO_DATA],
      value: 0,
    },
  };

  data.forEach((item) => {
    // if records array is less then skills count, add no data
    if (item.records.length < count) {
      result[MasteryPacing.NO_DATA].value += count - item.records.length;
    }
    item.records.forEach((r) => {
      if (r.pacing === MasteryPacing.AHEAD_OF_GRADE_LEVEL) {
        result[MasteryPacing.AHEAD_OF_GRADE_LEVEL].value++;
      } else if (r.pacing === MasteryPacing.AT_GRADE_LEVEL) {
        result[MasteryPacing.AT_GRADE_LEVEL].value++;
      } else if (r.pacing === MasteryPacing.BELOW_GRADE_LEVEL) {
        result[MasteryPacing.BELOW_GRADE_LEVEL].value++;
      }
    });
  });

  return Object.keys(result).map((key) => ({
    label: result[key as MasteryPacing].label,
    color: result[key as MasteryPacing].color,
    value: result[key as MasteryPacing].value,
  }));
};

// used for updating assignments array in slice state with new crud assignment responses
const updateAssignmentsThroughReplacement = (
  curAssignments: Assignment[],
  updatedAssignments: Assignment[],
  currentWeekStartDate: string,
  currentWeekEndDate: string
) => {
  let result = [...curAssignments];
  const updatedAssignmentsHash = new Map<string, Assignment>();

  updatedAssignments.forEach((ua) => {
    updatedAssignmentsHash.set(ua.lessonPlanId, ua);
    const foundIndex = result.findIndex(
      (assign) =>
        assign.entityType === ua.entityType &&
        assign.lessonPlanId === ua.lessonPlanId &&
        assign.entityId === ua.entityId
    );
    if (foundIndex !== -1) {
      if (ua.status !== AssignmentStatus.UNASSIGNED) {
        result[foundIndex] = ua;
      } else {
        result.splice(foundIndex, 1);
      }
    } else if (ua.status !== AssignmentStatus.UNASSIGNED) {
      result.push(ua);
    }
  });

  const weekStart = moment(currentWeekStartDate);
  const weekEnd = moment(currentWeekEndDate);

  result = result.filter(assignment => {
    const matchedAssignment = updatedAssignmentsHash.get(assignment.lessonPlanId);

    if (matchedAssignment) {
      const assignmentDate = moment(matchedAssignment.dateFrom);
      if (!assignmentDate.isBetween(weekStart, weekEnd, 'day', '[]')) {
        // If the updated/new assignment's dateFrom is NOT in the current week, remove it from the result.
        // This handles cases where an assignment might have been moved out of the current week.
        return false;
      }
    }
    return true;
  });

  return result;
};

const updateCurriculumManifest = (
  curriculum: PlannerCurriculum, 
  updatedAssignments: Assignment[], 
  students: { id: string; grade: string }[],
  currentWeekStartDate: string,
  currentWeekEndDate: string,
  mastery: { [key: string]: SkillMasteryRecord[] }
) => {
  // Start with a new object for tracks to allow modification
  let newTracks = curriculum.tracks.map(track => ({
    ...track,
    units: track.units.map(unit => ({
      ...unit,
      lessons: [...unit.lessons] // Clone lessons array for modification
    }))
  }));

  const weekStart = moment(currentWeekStartDate);
  const weekEnd = moment(currentWeekEndDate);
  const lessonPlanIdsToRemove = new Set<string>();

  // First, process manifest updates for assignments that ARE in the current week
  // And collect lessonPlanIds for assignments that are NOT in the current week
  updatedAssignments.forEach(ua => {
    const isUaInCurrentWeek = moment(ua.dateFrom).isBetween(weekStart, weekEnd, 'day', '[]');

    if (isUaInCurrentWeek) {
      // Assignment IS in the current week - update its manifest
      for (const track of newTracks) {
        for (const unit of track.units) {
          for (const lesson of unit.lessons) {
            if (lesson.lessonPlanId === ua.lessonPlanId) {
              if (ua.entityType === Entity.CLASS) {
                if (lesson.mainAssignment) { // Should ideally always exist
                  lesson.mainAssignment.manifest = ua.manifest;
                }
              } else if (ua.entityType === Entity.STUDENT) {
                const studentAsg = lesson.studentAssignments.find((sa: Assignment) => sa.entityId === ua.entityId);
                if (studentAsg) {
                  studentAsg.manifest = ua.manifest;
                } else {
                  // Optional: If a new student assignment for this lesson appears in updatedAssignments AND is in week, consider adding it.
                  // lesson.studentAssignments.push(mapUaToStudentAssignmentStructure(ua));
                }
              }
              break; // Found the lesson for this ua
            }
          }
        }
      }
    } else {
      // Assignment is NOT in the current week - mark its lessonPlanId for removal
      lessonPlanIdsToRemove.add(ua.lessonPlanId);
    }
  });

  // Now, filter out lessons, then empty units, then empty tracks
  if (lessonPlanIdsToRemove.size > 0) {
    newTracks = newTracks.map(track => ({
      ...track,
      units: track.units.map(unit => ({
        ...unit,
        lessons: unit.lessons.filter(lesson => !lessonPlanIdsToRemove.has(lesson.lessonPlanId))
      })).filter(unit => unit.lessons.length > 0) // Filter out units that became empty
    })).filter(track => track.units.length > 0); // Filter out tracks that became empty
  }
  
  const result = { 
    ...curriculum, // Spread other curriculum properties like name, dateFrom, dateTo
    tracks: newTracks 
  };

  recalculateTimeAssigned(result.tracks, students, mastery);
  return result;
};

const getResourcesByTags = (
  resources: { skillId: string; resources: AmiraResources[] }[],
  skills: string[],
  lessonName: string,
  curriculumId: string,
  resourcesTypes: ResourceType[]
) => {
  const resourcesByTags: {
    lessonId: Map<string, AmiraResources[]>;
    curriculumId: Map<string, AmiraResources[]>;
    skillId: Map<string, AmiraResources[]>;
  } = {
    lessonId: new Map(),
    curriculumId: new Map(),
    skillId: new Map(),
  };

  /**
   * FILTERS:
        SKILLDEFAULT-ASSIGNED
        CURR-<curriculumId>_DEFAULT (CURR-ufli)
        LESSONNAME-<lessonId> (LESSONNAME-A203882C8CB6C4816B873F35EA6DA382)

        Other tags:
          SKILL-<skillId> (SKILL-D.vowel_diphthong.oy_OY)
          STORY-<storyId>
   *
   * When I view district assigned activities for a skill,
   *  → I see activities that are tailored to the specific lesson I'm working on, if such activities exist.
   *
   * If there are no activities tied to my specific lesson,
   *  → I see activities that are aligned to my overall curriculum
   *
   * If neither lesson-specific nor curriculum-specific activities are available,
   *  → I see high-quality, standard default activities that cover the skill, so my students are always getting something appropriate.
   */
    resources.forEach(skillResources => {
      if (skills.includes(skillResources.skillId)) {
        skillResources.resources?.forEach(r => {
          if (resourcesTypes.includes(r.resourceType as ResourceType)) {
            if (r.tags.includes(`LESSONNAME-${lessonName}_DEFAULT`)) {
              if (!resourcesByTags.lessonId.has(skillResources.skillId)) {
                resourcesByTags.lessonId.set(skillResources.skillId, []);
              }
              resourcesByTags.lessonId.get(skillResources.skillId)?.push(r);
            } else if (r.tags.includes(`CURR-${curriculumId}_DEFAULT`)) {
              if (!resourcesByTags.curriculumId.has(skillResources.skillId)) {
                resourcesByTags.curriculumId.set(skillResources.skillId, []);
              }
              resourcesByTags.curriculumId.get(skillResources.skillId)?.push(r);
            } else if (r.tags.includes("SKILLDEFAULT-ASSIGNED")) {
              if (!resourcesByTags.skillId.has(skillResources.skillId)) {
                resourcesByTags.skillId.set(skillResources.skillId, []);
              }
              resourcesByTags.skillId.get(skillResources.skillId)?.push(r);
            }
          }
        });
      }
    });

  // make sure that we have resources for each skill
  const result: Map<string, AmiraResources[]> = new Map();
  for (const skillId of skills) {
    if (
      resourcesByTags.lessonId.size > 0 &&
      resourcesByTags.lessonId.has(skillId)
    ) {
      result.set(skillId, resourcesByTags.lessonId.get(skillId) || []);
    } else if (
      resourcesByTags.curriculumId.size > 0 &&
      resourcesByTags.curriculumId.has(skillId)
    ) {
      result.set(skillId, resourcesByTags.curriculumId.get(skillId) || []);
    } else if (
      resourcesByTags.skillId.size > 0 &&
      resourcesByTags.skillId.has(skillId)
    ) {
      result.set(skillId, resourcesByTags.skillId.get(skillId) || []);
    }
  }

  return result;
};

const convertResourcesToArray = (resources: Map<string, AmiraResources[]>) => {
  return Array.from(resources.entries()).map(([skillId, resources]) => ({
    skillId,
    resources,
  }));
};

const getDomainName = (studentId: string, skillId: string, mastery: SkillMasteryRecord[]) => {
  if (mastery) {
    const records = mastery.find(
      (record) => record.studentId === studentId
    );
    if (records && records.records && records.records.length) {
      const skillRecord = records.records.find(
        (r) => r.skillId === skillId
      );
      if (skillRecord) {
        return parseMasteryPacingToClassName(skillRecord.pacing);
      }
    }
  }  
  return parseMasteryPacingToClassName(null);
}

const recalculateTimeAssigned = (
  tracks: PlannerCurriculumTrack[],
  students: { id: string; grade: string }[],
  mastery: { [key: string]: SkillMasteryRecord[] }
) => {
  // Calculate class predominant grade once
  const classGrade = (getMostCommonGrade(students) ?? 1).toString();
  
  tracks.forEach(track => {
    track.units.forEach(unit => {
      unit.lessons.forEach(lesson => {
        lesson.timeAssigned = {}; // Reset per lesson

        // 🔁 Build reverse map: resourceId -> Set of skillIds
        const resourceToSkillMap = new Map<string, Set<string>>();
        const resourceInfoMap = new Map<string, { resourceType: string }>();

        lesson.resources.forEach(r => {
          r.resources.forEach(res => {
            const key = transformId(res.resourceUniqueId);
            if (!resourceToSkillMap.has(key)) {
              resourceToSkillMap.set(key, new Set());
              resourceInfoMap.set(key, { resourceType: res.resourceType || "" });
            }
            resourceToSkillMap.get(key)!.add(r.skillId);
          });
        });

        // 🔁 Per-student processing
        students.forEach(student => {
          const studentId = student.id;
          const skillTimes: Record<string, number> = {};

          const studentAssignment = lesson.studentAssignments.find(a => a.entityId === studentId);
          const hasStudentLevelAssignment =
            !!studentAssignment?.manifest?.length || !!studentAssignment?.resources?.length;
          if (studentAssignment?.assignmentType === AssignmentType.SKIP_OVERRIDE && !studentAssignment?.resources?.length) {
            // skip override means that the student has no activities for this skill
            // so we don't need to calculate time assigned for this skill
            // Edge case: if the student has resources, we still need to calculate time assigned for this skill
            return;
          }

          if (hasStudentLevelAssignment) {         
            // ▶️ 1. Use manifest items
            studentAssignment.manifest.forEach(m => {
              const skillId = m.skills?.[0]?.skillId;
              if (!skillId) return;

              const time = getTimeAssignedByGrade(m.activityType || "", [{ grade: classGrade }]);
              skillTimes[skillId] = (skillTimes[skillId] || 0) + time;
            });

            // ▶️ 2. Use all assigned resources → mapped to all relevant skills
            const assignedResourceIds = new Set(
              studentAssignment.resources?.map(transformId) || []
            );

            assignedResourceIds.forEach(resourceId => {
              const skillIds = resourceToSkillMap.get(resourceId);
              const resourceInfo = resourceInfoMap.get(resourceId);

              if (!skillIds || !resourceInfo) return;

              skillIds.forEach(skillId => {
                const time = getTimeAssignedByGrade(resourceInfo.resourceType, [{ grade: classGrade }]);
                skillTimes[skillId] = (skillTimes[skillId] || 0) + time;
              });
            });

          } else {
            // ▶️ 3. Fall back to class/district manifest
            const manifestBySkill = new Map<string, typeof lesson.mainAssignment.manifest>();

            lesson.mainAssignment?.manifest?.forEach(m => {
              const skillId = m.skills?.[0]?.skillId;
              if (!skillId) return;
              if (!manifestBySkill.has(skillId)) {
                manifestBySkill.set(skillId, []);
              }
              manifestBySkill.get(skillId)!.push(m);
            });

            manifestBySkill.forEach((items, skillId) => {
              const totalTime = items.reduce((sum, m) => {
                const time = getTimeAssignedByGrade(m.activityType || "", [{ grade: classGrade }]);
                return sum + time;
              }, 0);

              if (totalTime > 0) {
                skillTimes[skillId] = totalTime;
              }
            });

            // ▶️ 4. Add teacher resources (if DISTRICT or explicitly assigned)
            lesson.resources.forEach(r => {
              r.resources.forEach(res => {
                const skillId = r.skillId;
                const isDistrict = lesson.mainAssignment?.entityType === Entity.DISTRICT;
                const isExplicitlyAssigned =
                  lesson.mainAssignment?.resources?.map(transformId).includes(transformId(res.resourceUniqueId));

                if (isDistrict || isExplicitlyAssigned) {
                  const time = getTimeAssignedByGrade(res.resourceType || "", [{ grade: classGrade }]);
                  skillTimes[skillId] = (skillTimes[skillId] || 0) + time;
                }
              });
            });
          }

          // ▶️ 5. Assign to lesson.timeAssigned
          Object.entries(skillTimes).forEach(([skillId, time]) => {
            const domain = getDomainName(studentId, skillId, mastery[lesson.lessonPlanId]);
            if (!lesson.timeAssigned[skillId]) lesson.timeAssigned[skillId] = {};
            if (!lesson.timeAssigned[skillId][domain]) lesson.timeAssigned[skillId][domain] = {};
            lesson.timeAssigned[skillId][domain][studentId] = time;
          });
        });
      });
    });
  });
};



const WeeklyPlanSlice = createSlice({
  name: "weeklyPlan",
  initialState: initialWeeklyPlanState,
  reducers: {
    updateWeeklyPlanData(
      state,
      action: PayloadAction<Partial<WeeklyPlanStateData>>
    ) {
      if (action.payload) {
        state.data = {
          ...defaultWeeklyPlanState,
          ...state.data,
          ...action.payload,
        };
      }
    },
    updateWeeklyPlanDates(
      state,
      action: PayloadAction<Partial<WeeklyPlanStateData>>
    ) {
      if (action.payload && action.payload.dateTo && action.payload.dateFrom) {
        state.data = {
          ...defaultWeeklyPlanState,
          ...state.data,
          dateFrom: action.payload.dateFrom,
          dateTo: action.payload.dateTo,
        };
      }
    },
    updateLoadingState(state, action: PayloadAction<AsyncLoadingState>) {
      if (action.payload) {
        state.loading = action.payload;
        state.aggregateLessons = action.payload;
      }
    },
    updateAssignmentMutationLoadingState(
      state,
      action: PayloadAction<AsyncLoadingState>
    ) {
      if (action.payload) {
        state.mutateAssignmentsLoading = action.payload;
      }
    },
    updateResourcesQueue(
      state,
      action: PayloadAction<{ skillId: string; studentId: string; filter?: { providers?: string[] } }[]>
    ) {
      if (state.data) {
        state.data.resourcesFetchQueue = action.payload;
      }
    },
    updateSkillMasteryQueue(
      state,
      action: PayloadAction<
        {
          skillId: string[];
          studentId: string[];
          dateTo: string;
          dateFrom: string;
          lessonId: string;
        }[]
      >
    ) {
      if (state.data) {
        state.data.masteryFetchQueue = action.payload;
      }
    },
    resetWeekState(state) {
      if (state.data) {
        state.data.assignments = [];
        state.data.mastery = {};
        state.data.stories = {};
        state.data.searchedStories = [];
        state.data.curriculumInfo = [];
        state.data.resources = [];
        state.data.studentAssignmentInfo = [];
        state.data.curTrack = {
          label: "",
          value: "",
        };
        state.fetchTeacherAssignLoading = AsyncLoadingState.NONE;
        state.fetchStudentAssignLoading = AsyncLoadingState.NONE;
        state.skillsMasteryLoading = {};
        state.skillsMasteryLoaded = false;
        state.aggregateLessons = AsyncLoadingState.NONE;
        state.fetchCurriculumLoading = AsyncLoadingState.NONE;
        state.fetchSkillsMetadataLoading = AsyncLoadingState.NONE;
        state.error = null;
        // Clear initial fetch tracking when resetting week state
        state.initialFetchDispatched = {};
      }
    },
    updateCRUDAssignmentsQueue(
      state,
      action: PayloadAction<CRUDAssignmentQueue[]>
    ) {
      if (state.data) {
        state.data.crudAssignmentQueue = action.payload;
      }
    },
    createLessons(
      state,
      action: PayloadAction<{
        assignments: Assignment[];
        curriculumInfo: CurriculumLessonResponse[];
        dateTo: string;
        dateFrom: string;
        stories: { [key: string]: Story };
        studentCount: number;
      }>
    ) {
      if (state.data) {
        const curriculum = aggregateAssignmentsToLessons({ ...action.payload });
        if (curriculum) {
          state.data.curriculum = curriculum;
          if (
            state.data.curTrack.label === "" 
            && curriculum.tracks.length > 0 
            && curriculum.tracks[0].name 
            && curriculum.tracks[0].id
          ) {
            state.data.curTrack = {
              label: curriculum.tracks[0].name,
              value: curriculum.tracks[0].id,
            };
          }

          // Collect all unique skillIds from the curriculum
          const skillIds = new Set(
            curriculum.tracks
              .flatMap(track => track.units)
              .flatMap(unit => unit.lessons)
              .flatMap(lesson => lesson.skills.filter(s => s.skillType === SkillType.FOCUS))
              .map(skill => skill.skillId)
              .filter((id): id is string => !!id)
          )

          // Dispatch the fetchSkillsMetadata thunk with the collected skillIds
          if (skillIds.size > 0) {
            state.fetchSkillsMetadataLoading = AsyncLoadingState.LOADING;
            // The actual dispatch will be handled by the component using the slice
          }
        }
        state.aggregateLessons = AsyncLoadingState.SUCCESS;
      }
    },
    aggregateLessonsByLessonPlanIds(
      state,
      action: PayloadAction<{
        lessonPlanIds: string[];
        studentCount: number;
      }>
    ) {
      if (state.data) {
        const { assignments, curriculum } = state.data;
        if (assignments && curriculum) {
          let hasChanged = false;
          const { lessonPlanIds, studentCount } = action.payload;

          for (const lessonPlanId of lessonPlanIds) {
            const lessonData = aggregateUpdatedLessonByPlanId(
              assignments,
              curriculum,
              lessonPlanId,
              studentCount
            );
            if (!lessonData) continue;

            const track = curriculum.tracks.find(t => t.id === lessonData.trackId);
            const unit = track?.units.find(u => u.id === lessonData.unitId);
            const lessonIndex = unit?.lessons.findIndex(
              l => l.lessonPlanId === lessonData.lesson.lessonPlanId
            );

            if (lessonIndex != null && lessonIndex !== -1 && unit) {
              unit.lessons[lessonIndex] = lessonData.lesson;
              hasChanged = true;
            }
          }

          if (hasChanged) {
            state.data.curriculum = { ...curriculum };
          }

          state.data.crudResponseByLessonPlanId = [];
          state.mutateAssignmentsLoading = AsyncLoadingState.SUCCESS;
        }
      }
    },
    updateCurTrack(
      state,
      action: PayloadAction<{ label: string; value: string }>
    ) {
      if (state.data) {
        state.data.curTrack = action.payload;
      }
    },
    updateLessonResources(
      state,
      action: PayloadAction<{
        resources: { skillId: string; resources: AmiraResources[] }[];
        students: {id: string, grade: string}[];
      }>
    ) {
      if (state.data) {
        state.data.curriculum.tracks.forEach(t => {
          t.units.forEach(u => {
            u.lessons.forEach(l => {
              l.timeAssigned = {};
              const focusSkills = l.skills.filter(s => s.skillType === SkillType.FOCUS);
              const focusSkillIds = focusSkills.map(s => s.skillId || "");
              // if an assignment is a district assignment, we use the skill resources api (use the "default" resources)
              if (l.mainAssignment.entityType === Entity.DISTRICT) {
                const teacherResources = getResourcesByTags(
                  action.payload.resources,
                  focusSkillIds,
                  l.mainAssignment.lessonName,
                  state.data?.curriculum.name || "",
                  [ResourceType.teacherResource]
                );

                // we just want to get the resources that are assignable to the lesson
                // and which are with focus skills
                const assignableResources = getResourcesByTags(
                  action.payload.resources,
                  focusSkillIds,
                  l.mainAssignment.lessonName,
                  state.data?.curriculum.name || "",
                  [
                    ResourceType.EPS,
                    ResourceType.ERSS,
                    ResourceType.textSet,
                    ResourceType.tutor,
                    ResourceType.microLesson,
                    ResourceType.istationLegacyActivity,
                    ResourceType.skillTutor,
                  ]
                );
                const convertedTeacherResources = convertResourcesToArray(teacherResources);
                l.resources = convertedTeacherResources;

                // Convert maps to new timeAssigned map
                let mappedAssignableResources: Array<{
                  skillId: string;
                  resources: AmiraResources[];
                }> = [];
                mappedAssignableResources =
                  convertResourcesToArray(assignableResources);
                
                const defaultManifest = getDefaultManifest(
                  mappedAssignableResources,
                  focusSkills
                );

                // Assign a default DISTRICT manifest
                l.mainAssignment.manifest = defaultManifest.length > 0 ? defaultManifest : l.mainAssignment.manifest;
                l.mainAssignment.resources = convertedTeacherResources.map(r => r.resources.map(res => res.resourceUniqueId)).flat();
              }  
              if (l.studentAssignments.length > 0) {
                // If there are class or student assignments, use assignment.resources (use saved teacher resources)
                // lesson assignments with resources will have resources IDs in the mainAssignment.resources array
                // filter the resources by the resource IDs in the mainAssignment.resources array
                if (l.mainAssignment.resources) {
                  const resourceData = action.payload.resources
                    .map(skillResources => {
                      const resources = skillResources.resources?.filter(
                        r =>
                          l.mainAssignment.resources?.indexOf(
                            transformId(r.resourceUniqueId) || ""
                          ) !== -1
                      );
                      if (resources.length > 0) {
                        return {
                          ...skillResources,
                          resources: resources,
                        };
                      }
                      return null;
                    })
                    .filter(r => r !== null);

                  if (resourceData.length > 0) {
                    l.resources = resourceData;
                  }
                }

                l.studentAssignments.forEach(s => {
                  if (s.resources && s.resources.length > 0) {
                    const resourceData = action.payload.resources
                      .map(skillResources => {
                        const filteredResources =
                          skillResources.resources?.filter(
                            r =>
                              s.resources?.indexOf(
                                transformId(r.resourceUniqueId) || ""
                              ) !== -1
                          ) || [];

                        if (filteredResources.length > 0) {
                          return {
                            ...skillResources,
                            resources: filteredResources,
                          };
                        }
                        return null;
                      })
                      .filter(item => item !== null);

                    if (resourceData.length > 0) {
                      resourceData.forEach(r => {
                        if (
                          !l.resources.find(res => res.skillId === r.skillId)
                        ) {
                          l.resources.push(r);
                        } else {
                          const lessonResources = structuredClone(l.resources)
                          const targetResourceGroup = lessonResources.find(
                            res => res.skillId === r.skillId
                          );
                          if (
                            targetResourceGroup &&
                            targetResourceGroup.resources
                          ) {
                            // Build a Set of existing resourceUniqueId values for O(1) lookup
                            const existingResourceIds = new Set(
                              targetResourceGroup.resources.map(
                                existingRes => transformId(existingRes.resourceUniqueId) || ""
                              )
                            );

                            // Filter new resources in O(n) time
                            const uniqueNewResources = r.resources.filter(
                              newRes => !existingResourceIds.has(transformId(newRes.resourceUniqueId) || "")
                            );

                            // Push only the unique resources
                            targetResourceGroup.resources.push(...uniqueNewResources);
                            l.resources = lessonResources;
                          }
                        }
                      });
                    }
                  }
                });
              }
            });
          });
        });
        state.data.students = action.payload.students.map(s => ({id: s.id, grade: s.grade}));
        recalculateTimeAssigned(state.data?.curriculum.tracks || [], state.data.students, state.data.mastery);
      }
    },
    updateSkillsMetadata(
      state,
      action: PayloadAction<{ skillId: string; metadata: SkillMetadata }[]>
    ) {
      if (state.data) {
        const metadataMap = new Map(action.payload.map(({ skillId, metadata }) => [skillId, metadata]));
        for (const track of state.data.curriculum.tracks)
          for (const unit of track.units)
            for (const lesson of unit.lessons)
              for (const skill of lesson.skills) {
                if (!skill.skillId || skill.skillType !== SkillType.FOCUS) continue;
                const metadata = metadataMap.get(skill.skillId);
                if (metadata) Object.assign(skill, {
                  timeToMaster: Math.ceil(metadata.timeToMaster/60),
                  preReqSkills: metadata.preReqSkills
                });
              }
      }
    },
    markInitialFetchDispatched(
      state,
      action: PayloadAction<{ classroomId: string }>
    ) {
      state.initialFetchDispatched[action.payload.classroomId] = true;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(
        fetchTeacherAssignments.fulfilled,
        (state, action: PayloadAction<GetTeacherAssignmentsResponse>) => {
          state.fetchTeacherAssignLoading = AsyncLoadingState.SUCCESS;
          state.skillsMasteryLoaded = false;
          state.skillsMasteryLoading = {};

          if (!state.data) {
            state.data = { ...defaultWeeklyPlanState };
          }
          // add to rather than replace
          const allAssignments = [
            ...state.data.assignments,
            ...action.payload.assignment,
          ];
          const dedupedAssignments = Array.from(
            new Map(allAssignments.map(a => [a.assignmentId, a])).values()
          );
          state.data.assignments = dedupedAssignments;
        }
      )
      .addCase(fetchTeacherAssignments.pending, state => {
        if (state.data) {
          state.data.curriculum = {
            name: "",
            id: "",
            dateFrom,
            dateTo,
            tracks: [],
          };
        }
        state.fetchTeacherAssignLoading = AsyncLoadingState.LOADING;
      })
      .addCase(fetchTeacherAssignments.rejected, (state, action) => {
        state.fetchTeacherAssignLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(
        fetchStudentAssignments.fulfilled,
        (state, action: PayloadAction<GetStudentAssignmentsResponse>) => {
          state.fetchStudentAssignLoading = AsyncLoadingState.SUCCESS;
          if (!state.data) {
            state.data = { ...defaultWeeklyPlanState };
          }

          // add to rather than replace
          const allAssignments = [
            ...state.data.assignments,
            ...action.payload.teacherClassInbox.assignments,
          ];
          const dedupedAssignments = Array.from(
            new Map(allAssignments.map(a => [a.assignmentId, a])).values()
          );
          state.data.assignments = dedupedAssignments;
          // full replace for now
          state.data.studentAssignmentInfo = [
            ...action.payload.teacherClassInbox.studentAssignments,
          ];
        }
      )
      .addCase(fetchStudentAssignments.pending, state => {
        if (state.data) {
          state.data.curriculum = {
            name: "",
            id: "",
            dateFrom,
            dateTo,
            tracks: [],
          };
        }
        state.fetchStudentAssignLoading = AsyncLoadingState.LOADING;
      })
      .addCase(fetchStudentAssignments.rejected, (state, action) => {
        state.fetchStudentAssignLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(fetchCurriculum.fulfilled, (state, action) => {
        state.fetchCurriculumLoading = AsyncLoadingState.SUCCESS;
        if (state.data) {
          state.data.curriculumInfo =
            action.payload.getCurriculumInfoForLessons;
        }
      })
      .addCase(fetchCurriculum.pending, state => {
        state.fetchCurriculumLoading = AsyncLoadingState.LOADING;
      })
      .addCase(fetchCurriculum.rejected, (state, action) => {
        state.fetchCurriculumLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(batchCreateStudentAssignments.fulfilled, (state, action) => {
        if (!state.data) {
          state.data = { ...defaultWeeklyPlanState };
        }
        if (action.payload.batchAddAssignment.length) {
          state.data.assignments = updateAssignmentsThroughReplacement(
            state.data.assignments,
            action.payload.batchAddAssignment,
              state.data.dateFrom,
              state.data.dateTo
          );
          state.data.curriculum = updateCurriculumManifest(state.data.curriculum, action.payload.batchAddAssignment, state.data.students, state.data.dateFrom, state.data.dateTo, state.data.mastery);
          action.payload.batchAddAssignment.forEach(assign => {
            if (
              state.data!.crudResponseByLessonPlanId.findIndex(
                lpi => lpi === assign.lessonPlanId
              ) === -1
            ) {
              state.data!.crudResponseByLessonPlanId.push(assign.lessonPlanId);
            }
          });
        }
      })
      .addCase(batchCreateStudentAssignments.pending, state => {
        state.mutateAssignmentsLoading = AsyncLoadingState.LOADING;
      })
      .addCase(batchCreateStudentAssignments.rejected, (state, action) => {
        state.mutateAssignmentsLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(batchUnassignAssignments.fulfilled, (state, action) => {
        if (!state.data) {
          state.data = { ...defaultWeeklyPlanState };
        }
        if (action.payload.batchUpdateAssignment.length) {
          state.data.assignments = updateAssignmentsThroughReplacement(
            state.data.assignments,
            action.payload.batchUpdateAssignment,
            state.data.dateFrom,
            state.data.dateTo
          );
          state.data.curriculum = updateCurriculumManifest(state.data.curriculum, action.payload.batchUpdateAssignment, state.data.students, state.data.dateFrom, state.data.dateTo, state.data.mastery);
          action.payload.batchUpdateAssignment.forEach(assign => {
            if (
              state.data!.crudResponseByLessonPlanId.findIndex(
                lpi => lpi === assign.lessonPlanId
              ) === -1
            ) {
              state.data!.crudResponseByLessonPlanId.push(assign.lessonPlanId);
            }
          });
        }
      })
      .addCase(batchUnassignAssignments.pending, state => {
        state.mutateAssignmentsLoading = AsyncLoadingState.LOADING;
      })
      .addCase(batchUnassignAssignments.rejected, (state, action) => {
        state.mutateAssignmentsLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(batchUpdateAssignments.fulfilled, (state, action) => {
        if (!state.data) {
          state.data = { ...defaultWeeklyPlanState };
        }
        if (action.payload.batchUpdateAssignment.length) {
          state.data.assignments = updateAssignmentsThroughReplacement(
            state.data.assignments,
            action.payload.batchUpdateAssignment,
            state.data.dateFrom,
            state.data.dateTo
          );
          state.data.curriculum = updateCurriculumManifest(state.data.curriculum, action.payload.batchUpdateAssignment, state.data.students, state.data.dateFrom, state.data.dateTo, state.data.mastery);
          action.payload.batchUpdateAssignment.forEach(assign => {
            if (
              state.data!.crudResponseByLessonPlanId.findIndex(
                lpi => lpi === assign.lessonPlanId
              ) === -1
            ) {
              state.data!.crudResponseByLessonPlanId.push(assign.lessonPlanId);
            }
          });
        }
      })
      .addCase(batchUpdateAssignments.pending, state => {
        state.mutateAssignmentsLoading = AsyncLoadingState.LOADING;
      })
      .addCase(batchUpdateAssignments.rejected, (state, action) => {
        state.mutateAssignmentsLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        if (!state.data) {
          state.data = { ...defaultWeeklyPlanState };
        }
        if (action.payload.addAssignment) {
          state.data.assignments = updateAssignmentsThroughReplacement(
            state.data.assignments,
            [action.payload.addAssignment],
            state.data.dateFrom,
            state.data.dateTo
          );
          state.data.curriculum = updateCurriculumManifest(state.data.curriculum, [action.payload.addAssignment], state.data.students, state.data.dateFrom, state.data.dateTo, state.data.mastery);
          if (
            state.data!.crudResponseByLessonPlanId.findIndex(
              lpi => lpi === action.payload.addAssignment.lessonPlanId
            ) === -1
          ) {
            state.data!.crudResponseByLessonPlanId.push(
              action.payload.addAssignment.lessonPlanId
            );
          }
        }
      })
      .addCase(createAssignment.pending, state => {
        state.mutateAssignmentsLoading = AsyncLoadingState.LOADING;
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.mutateAssignmentsLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(fetchSkillResources.fulfilled, (state, action) => {
        if (state.data) {
          // TEMPORARY FIX: Replace "Bayuo" with "Bayou" in resource titles
          const fixedResources = (action.payload.resources as AmiraResources[]).map(resource => ({
            ...resource,
            title: resource.title ? resource.title.replace(/Bayuo/g, 'Bayou') : resource.title
          }));

          // Check if a resource with this skillId already exists
          const existingResourceIndex = state.data.resources.findIndex(
            resource => resource.skillId === action.payload.skillId
          );
          if (existingResourceIndex !== -1) {
            // Merge new resources with existing ones
            const existingResources =
              state.data.resources[existingResourceIndex].resources;
            const newResources = fixedResources;
            
            // Build a Set of existing resourceUniqueId values for O(1) lookup
            const existingResourceIds = new Set(
              existingResources.map(resource => resource.resourceUniqueId)
            );
            
            // Filter new resources in O(n) time
            const uniqueNewResources = newResources.filter(
              newResource => !existingResourceIds.has(newResource.resourceUniqueId)
            );
            
            state.data.resources[existingResourceIndex].resources = [
              ...existingResources,
              ...uniqueNewResources,
            ];
          } else {
            // Only push if it doesn't already exist
            state.data.resources.push({
              skillId: action.payload.skillId,
              resources: fixedResources,
            });
          }
        }
        state.fetchSkillResourcesLoading -= 1;
      })
      .addCase(fetchSkillResources.pending, state => {
        state.fetchSkillResourcesLoading += 1;
      })
      .addCase(fetchSkillResources.rejected, (state, action) => {
        state.fetchSkillResourcesLoading -= 1;
        state.error = action.payload as string;
      })
      .addCase(fetchResourcesMetadata.fulfilled, (state, action) => {
        if (state.data) {
          const metadataMap = new Map(
            action.payload.resourcesMetadata.map(
              ({ resourceUniqueId, ...metadata }) => [
                transformId(resourceUniqueId), // IDs on the backend are inconsistent
                {
                  ...metadata,
                  // TEMPORARY FIX: Replace "Bayuo" with "Bayou" in resource metadata titles
                  title: metadata.title ? metadata.title.replace(/Bayuo/g, 'Bayou') : metadata.title
                },
              ]
            )
          );

          state.data.resources.forEach(resourceGroup => {
            resourceGroup.resources.forEach(resource => {
              const metadata = metadataMap.get(transformId(resource.resourceUniqueId));
              if (metadata) {
                Object.assign(resource, metadata);
              }
            });
          });
        }
        state.fetchResourcesMetadataLoading = AsyncLoadingState.SUCCESS;
      })
      .addCase(fetchResourcesMetadata.pending, state => {
        state.fetchResourcesMetadataLoading = AsyncLoadingState.LOADING;
      })
      .addCase(fetchResourcesMetadata.rejected, (state, action) => {
        state.fetchResourcesMetadataLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(fetchStudentMastery.fulfilled, (state, action) => {
        if (state.data) {
          state.data.mastery[action.payload.lessonId] =
            action.payload.studentMastery;
        }
        state.fetchStudentMasteryLoading -= 1;
      })
      .addCase(fetchStudentMastery.pending, state => {
        state.fetchStudentMasteryLoading += 1;
      })
      .addCase(fetchStudentMastery.rejected, (state, action) => {
        state.fetchStudentMasteryLoading -= 1;
        state.error = action.payload as string;
      })
      .addCase(
        fetchStoryData.fulfilled,
        (state, action: PayloadAction<GetStoryResponse>) => {
          // handle the fulfilled state
          if (action.payload && action.payload.getStoryGuide) {
            const story = action.payload.getStoryGuide;

            if (state.data) {
              state.data.searchedStories = [story];
            }

            state.fetchSearchStoriesLoading = AsyncLoadingState.SUCCESS;
          }
        }
      )
      .addCase(fetchStoryData.pending, state => {
        // handle the pending state
        state.fetchSearchStoriesLoading = AsyncLoadingState.LOADING;
      })
      .addCase(fetchStoryData.rejected, (state, action) => {
        // handle the rejected state
        state.fetchSearchStoriesLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(fetchStudentSkillsMastery.fulfilled, (state, action) => {
        state.skillsMasteryLoaded = true;

        if (!state.data) {
          state.data = { ...defaultWeeklyPlanState };
        }
        const {
          lessonStartResponse,
          lessonTodayResponse,
          lessonPlanId,
          skillsCount,
        } = action.payload;

        state.skillsMasteryLoading[lessonPlanId] = false;

        if (
          lessonStartResponse.studentSkillsMastery.length === 0 ||
          lessonTodayResponse.studentSkillsMastery.length === 0
        ) {
          return; // NO DATA
        }

        const chartData: ChangeInMastery[] = [];
        chartData.push({
          label: "LESSON_START",
          data: calculatePacing(
            lessonStartResponse.studentSkillsMastery,
            skillsCount
          ),
        });
        chartData.push({
          label: "TODAY",
          data: calculatePacing(
            lessonTodayResponse.studentSkillsMastery,
            skillsCount
          ),
        });

        state.data.curriculum.tracks.map(t => {
          t.units.map(u => {
            u.lessons.map(l => {
              if (l.lessonPlanId === lessonPlanId) {
                l.masteryData = chartData;
              }
              return l;
            });
            return u;
          });
          return t;
        });
      })
      .addCase(fetchStudentSkillsMastery.pending, (state, action) => {
        state.skillsMasteryLoading[action.meta.arg.lessonPlanId] = true;
      })
      .addCase(fetchStudentSkillsMastery.rejected, (state, action) => {
        state.skillsMasteryLoading[action.meta.arg.lessonPlanId] = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBatchStudentSkillStandards.fulfilled, (state, action) => {
        if (!state.data) {
          state.data = { ...defaultWeeklyPlanState };
        }
        
        const { responses } = action.payload;
        
        if (responses.length === 0) {
          return; // NO DATA to process
        }

        // Group responses by lessonPlanId for efficient updating
        const responsesByLesson = new Map<string, typeof responses>();
        responses.forEach(response => {
          const lessonPlanId = response.request.lessonPlanId;
          if (!responsesByLesson.has(lessonPlanId)) {
            responsesByLesson.set(lessonPlanId, []);
          }
          responsesByLesson.get(lessonPlanId)!.push(response);
        });

        // Update curriculum lessons with skill standards
        state.data.curriculum.tracks.forEach(track => {
          track.units.forEach(unit => {
            unit.lessons.forEach(lesson => {
              const lessonResponses = responsesByLesson.get(lesson.lessonPlanId);
              if (lessonResponses) {
                lessonResponses.forEach(({ data: skillStandards, request }) => {
                  if (skillStandards.length > 0) {
                    if (lesson.skillStandards) {
                      // Avoid duplicates when adding new standards
                      const existingSkillIds = new Set(
                        lesson.skillStandards.map(ss => ss.skill?.skillId).filter(Boolean)
                      );
                      const newStandards = skillStandards.filter(
                        ss => !existingSkillIds.has(ss.skill?.skillId)
                      );
                      lesson.skillStandards.push(...newStandards);
                    } else {
                      lesson.skillStandards = skillStandards;
                    }
                  }
                });
              }
            });
          });
        });
      })
      .addCase(fetchBatchStudentSkillStandards.pending, (state, action) => {
        // Optional: Add loading state tracking if needed
      })
      .addCase(fetchBatchStudentSkillStandards.rejected, (state, action) => {
        state.error = action.payload as string;
      }) 
      .addCase(fetchSkillsMetadata.fulfilled, (state, action: PayloadAction<SkillMetadata[]>) => {
        state.fetchSkillsMetadataLoading = AsyncLoadingState.SUCCESS;
        state.error = null; // Clear any previous errors
        if (state.data) {
          // Prepare the payload for the updateSkillsMetadata reducer
          const preparedPayload = action.payload.map(metadata => ({
            skillId: metadata.skillId,
            metadata: metadata, // metadata here is the full SkillMetadata object
          }));

          // Call the updateSkillsMetadata reducer logic directly
          // The type for the action passed to the case reducer needs to match its definition
          WeeklyPlanSlice.caseReducers.updateSkillsMetadata(state, {
            type: "weeklyPlan/updateSkillsMetadata",
            payload: preparedPayload,
          });
        }
      })
      .addCase(fetchSkillsMetadata.pending, state => {
        state.fetchSkillsMetadataLoading = AsyncLoadingState.LOADING;
        state.error = null; // Clear any previous errors
      })
      .addCase(fetchSkillsMetadata.rejected, (state, action) => {
        state.fetchSkillsMetadataLoading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;

        // Attempt to recover by retrying with exponential backoff
        if (state.data) {
          const retryCount = (state.data as any).metadataRetryCount || 0;
          if (retryCount < 3) {
            // Max 3 retries
            (state.data as any).metadataRetryCount = retryCount + 1;
            setTimeout(
              () => {
                // This will be handled by the component using the slice
                state.fetchSkillsMetadataLoading = AsyncLoadingState.NONE;
              },
              Math.pow(2, retryCount) * 1000
            ); // Exponential backoff: 1s, 2s, 4s
          }
        }
      });
  },
});

// Export the actions
export const {
  updateWeeklyPlanData,
  updateWeeklyPlanDates,
  updateLoadingState,
  resetWeekState,
  createLessons,
  updateCurTrack,
  updateResourcesQueue,
  updateSkillMasteryQueue,
  updateLessonResources,
  updateAssignmentMutationLoadingState,
  updateCRUDAssignmentsQueue,
  aggregateLessonsByLessonPlanIds,
  updateSkillsMetadata,
  markInitialFetchDispatched,
} = WeeklyPlanSlice.actions;
export const selectWeeklyPlan = (state: RootState) => state.weeklyPlan;
export const selectWeeklyPlanData = (state: RootState) => state.weeklyPlan.data as WeeklyPlanStateData;
export const selectWeeklyPlanIsLoading = (state: RootState) => {
  if (state.weeklyPlan.error) {
    // if any error, we don't want to show the loading state
    return false;
  }
  if (state.weeklyPlan) {
    const {
      fetchStudentAssignLoading,
      fetchTeacherAssignLoading,
      fetchCurriculumLoading,
      fetchSkillsMetadataLoading,
      data,
      fetchStudentMasteryLoading,
    } = state.weeklyPlan;
    if (fetchCurriculumLoading === AsyncLoadingState.SUCCESS && data?.curriculum.tracks.length === 0) {
      // if the curriculum is empty, we don't want to show the loading state
      return false;
    }
    return !(
      fetchStudentAssignLoading === AsyncLoadingState.SUCCESS &&
      fetchTeacherAssignLoading === AsyncLoadingState.SUCCESS &&
      fetchCurriculumLoading === AsyncLoadingState.SUCCESS &&
      fetchSkillsMetadataLoading === AsyncLoadingState.SUCCESS &&
      fetchStudentMasteryLoading === 0 &&
      ((data?.assignments.length && data?.curriculum.tracks.length) ||
        (!data?.assignments.length && !data?.curriculum.tracks.length))
    );
  }
  return true;
};
export const selectResourcesLoading = (state: RootState) => {
  if (state.weeklyPlan.error) {
    return false;
  }
  if (state.weeklyPlan) {
    const {
      fetchSkillResourcesLoading,
      fetchResourcesMetadataLoading,
      fetchCurriculumLoading,
      data
    } = state.weeklyPlan;
    if (fetchCurriculumLoading === AsyncLoadingState.SUCCESS && data?.curriculum.tracks.length === 0) {
      // if the curriculum is empty, we don't want to show the loading state
      return false;
    }
    return !(
      fetchResourcesMetadataLoading === AsyncLoadingState.SUCCESS &&
      fetchSkillResourcesLoading === 0
    );
  }
  return true;
};
export const selectMutateAssignmentsLoading = (state: RootState) => state.weeklyPlan.mutateAssignmentsLoading === AsyncLoadingState.LOADING;
export const selectSkillsMasteryLoading = (state: RootState) => state.weeklyPlan.skillsMasteryLoading;

const selectLessonSkills = (state: RootState, lessonId: string) => {
  // Logic to find the specific lesson's skills array by lessonId
  return state.weeklyPlan.data?.curriculum.tracks.flatMap(t => t.units.flatMap(u => u.lessons.find(l => l.lessonPlanId === lessonId)?.skills)) ?? [];
};

export const selectLessonFocusSkills = createSelector(
  [selectLessonSkills],
  (skills) => (skills as Skill[]).filter(({skillType}) => skillType === SkillType.FOCUS)
);

// Helper function to check if skill standards exist (exported for component use)
export const selectHasSkillStandards = (state: RootState, skillId: string, studentId: string, lessonPlanId: string): boolean => {
  const curriculum = state.weeklyPlan.data?.curriculum;
  if (!curriculum) return false;
  return hasSkillStandardsInStore(curriculum, skillId, studentId, lessonPlanId);
};

// Selector to get skill standards from store
export const selectSkillStandardsForSkill = (state: RootState, skillId: string, lessonPlanId: string): SkillStandardResponse[] => {
  const curriculum = state.weeklyPlan.data?.curriculum;
  if (!curriculum) return [];
  
  for (const track of curriculum.tracks) {
    for (const unit of track.units) {
      for (const lesson of unit.lessons) {
        if (lesson.lessonPlanId === lessonPlanId && lesson.skillStandards) {
          return lesson.skillStandards.filter(ss => ss.skill?.skillId === skillId);
        }
      }
    }
  }
  return [];
};

export default WeeklyPlanSlice.reducer;
